import { Link, useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import Layout from '../components/Layout';
import GlobeSimple from '../components/GlobeSimple';
import TeamSection from '../components/TeamSection';
import { motion } from "framer-motion";
import { 
  FaShieldAlt, 
  FaSearch, 
  FaExclamationTriangle, 
  FaBookOpen, 
  FaTools,
  FaUserShield,
  FaChartLine,
  FaRocket,
  FaBriefcase,
  FaLightbulb
} from 'react-icons/fa';
import { useEffect, useState } from 'react';
import ParticleBackground from '../components/ParticleBackground';

const TYPING_WORDS = [
  "detect misinformation",
  "analyze content",
  "verify sources",
  "empower truth",
];

const Index = () => {
  // Typing animation state
  const [wordIndex, setWordIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [typing, setTyping] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const currentWord = TYPING_WORDS[wordIndex];
    if (typing) {
      if (displayed.length < currentWord.length) {
        timeout = setTimeout(() => setDisplayed(currentWord.slice(0, displayed.length + 1)), 70);
      } else {
        timeout = setTimeout(() => setTyping(false), 1200);
      }
    } else {
      if (displayed.length > 0) {
        timeout = setTimeout(() => setDisplayed(currentWord.slice(0, displayed.length - 1)), 30);
      } else {
        setTyping(true);
        setWordIndex((wordIndex + 1) % TYPING_WORDS.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [displayed, typing, wordIndex]);

  return (
    <Layout fullWidth={true}>
      {/* Modern Hero Section */}
      <section className="relative min-h-[80vh] flex flex-col-reverse md:flex-row items-center justify-center overflow-hidden w-full bg-gradient-to-br from-[#0a102a] via-[#101a3c] to-[#0a102a] font-sans px-2 sm:px-4 py-4 md:py-10">
        {/* Particle background animation */}
        <ParticleBackground />
        {/* Left: Hero text and buttons */}
        <div className="relative z-10 flex-1 flex flex-col items-center md:items-start justify-center px-2 sm:px-6 md:px-16 py-10 md:py-16 text-center md:text-left w-full max-w-full">
          <motion.h1 
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: 'easeOut' }}
            className="text-4xl xs:text-5xl sm:text-6xl md:text-8xl xl:text-9xl font-extrabold leading-tight mb-2 sm:mb-4 drop-shadow-xl tracking-tight"
            style={{ fontFamily: 'Inter, SF Pro, sans-serif', letterSpacing: '-0.03em' }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 animate-gradient">
              Truth Nexus
            </span>
          </motion.h1>
          {/* Typing animation below heading */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="h-[2.5em] flex items-center mb-4 sm:mb-6"
          >
            <span className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-bold text-white/90 min-h-[1em]" style={{ fontFamily: 'Inter, SF Pro, sans-serif' }}>
              {displayed}&nbsp;
              <span className="inline-block w-2 h-8 align-middle bg-cyan-400 animate-blink rounded-sm ml-1" />
            </span>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg xs:text-xl sm:text-2xl md:text-3xl xl:text-4xl text-blue-100 mb-6 sm:mb-10 max-w-xl mx-auto md:mx-0 font-medium"
            style={{ fontFamily: 'Inter, SF Pro, sans-serif' }}
          >
            Harness the power of AI to detect, analyze, and combat misinformation across the digital landscape.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="flex flex-col xs:flex-row gap-4 sm:gap-6 w-full xs:w-auto justify-center md:justify-start mb-6 sm:mb-8"
          >
            <Button asChild size="lg" className="rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 hover:from-blue-500 hover:to-cyan-400 text-base sm:text-xl px-6 sm:px-10 py-4 sm:py-5 shadow-xl shadow-cyan-400/20 border-0 font-semibold transition-all duration-300">
              <Link to="/auth/register" className="flex items-center gap-2">
                Get Started <FaRocket className="ml-1" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="rounded-full text-base sm:text-xl px-6 sm:px-10 py-4 sm:py-5 border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/10 transition-all duration-300 font-semibold shadow-lg bg-black/30"
              onClick={() => navigate('/auth/signin')}
            >
              Sign In
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-2 text-xs sm:text-base text-cyan-200/80 font-medium"
          >
            Unmasking the Truth, One Byte at a Time.<br />
            <span className="text-cyan-400 animate-float">AI-powered. Fact-driven. Misinformation Defeated.</span>
          </motion.div>
        </div>
        {/* Right: 3D Globe with glow and parallax placeholder */}
        <div className="relative z-10 flex-1 items-center justify-center w-full h-[250px] xs:h-[320px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] max-w-full hidden md:flex">
          <GlobeSimple />
        </div>
      </section>

      {/* Founder section with premium design */}
      <section className="py-24 bg-black/60 backdrop-blur-md w-full glass-card border-y border-truth/10">
        <div className="w-[95%] md:w-[90%] lg:max-w-[1500px] mx-auto px-4">
          <motion.div 
            className="flex flex-col lg:flex-row items-center gap-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="lg:w-1/3">
              <div className="relative w-64 h-64 mx-auto lg:mx-0 ambient-light">
                <div className="absolute inset-0 bg-truth/30 rounded-full blur-xl"></div>
                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-2xl -z-10 animate-pulse-slow"></div>
                <div className="absolute inset-0 rounded-full overflow-hidden border-2 border-truth glow-lg pulse-glow">
                  {/* Placeholder founder image with premium effect */}
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 flex items-center justify-center reflective-surface">
                    <span className="text-6xl text-truth font-bold glow-text relative z-10">WG</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-2/3 text-center lg:text-left">
              <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
                Founder's Message
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                From Vision to Reality
              </h2>
              <p className="text-gray-300 text-lg mb-4">
                Truth Nexus Global AI was founded by <span className="text-truth font-medium">Wobyeb Graphlain</span>, 
                a visionary technologist.He is dedicated to fighting disinformation using advanced artificial intelligence.
              </p>
              <p className="text-gray-300 mb-6">
                "Our mission is to create a more informed digital landscape where truth prevails over manipulation.
                We're developing cutting-edge technologies that can detect and counter misinformation at scale,
                while empowering individuals with tools to verify information for themselves."
              </p>
              <div className="p-4 glass-card border border-truth/20 rounded-lg shadow-lg">
                <h3 className="text-xl font-semibold text-truth">Investment Opportunity</h3>
                <p className="text-gray-300">
                  We are currently seeking strategic investors who share our vision which is to create a 
                  misinformation free digital world. Join us in scaling our revolutionary AI Approach.
                </p>
                <Button asChild className="mt-3 bg-truth hover:bg-truth-dark">
                <a href="mailto:wobyebgraphlin90@gmail.com">Connect with Our Founder</a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section with enhanced premium UI */}
      <section className="py-24 w-full premium-gradient relative overflow-hidden w-full">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-truth/5 to-transparent"></div>
        <div className="w-full mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
              Our Features
            </div>
            <h2 className="text-4xl font-bold mb-4 text-white">What We Offer</h2>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Our platform offers a comprehensive suite of tools and services to combat misinformation.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 w-full">
            <FeatureCard 
              icon={<FaShieldAlt />}
              title="Source Credibility Scoring"
              description="Evaluate news sources with our comprehensive credibility scoring system that analyzes historical accuracy and transparency."
              delay={0.2}
            />
            <FeatureCard 
              icon={<FaExclamationTriangle />}
              title="Deepfake Detection"
              description="Identify AI-generated or manipulated media with our state-of-the-art detection algorithms."
              delay={0.4}
            />
            <FeatureCard 
              icon={<FaBookOpen />}
              title="Media Literacy Tools"
              description="Interactive learning experiences that teach critical evaluation skills to help users identify disinformation tactics."
              delay={0.6}
            />
            <FeatureCard 
              icon={<FaTools />}
              title="OSINT Research Tools"
              description="Advanced open-source intelligence tools for tracking and investigating disinformation campaigns across platforms."
              delay={0.8}
            />
            <FeatureCard 
              icon={<FaUserShield />}
              title="Vulnerability Scanning"
              description="Security tools that scan systems and applications for vulnerabilities that hackers might exploit."
              delay={1.0}
            />
          </div>
        </div>
      </section>

      {/* Stats Section with enhanced animations */}
      <section className="py-24 w-full bg-black/40 backdrop-blur-sm relative overflow-hidden w-full">
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <div className="w-full mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
              Our Impact
            </div>
            <h2 className="text-4xl font-bold mb-4 text-white">Making a Difference</h2>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Our technologies have already made significant strides in combating misinformation worldwide.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 w-full">
            <StatCard number="100+" label="Content Analyses" icon={<FaChartLine />} />
            <StatCard number="95%" label="Detection Accuracy" icon={<FaSearch />} />
            <StatCard number="1+" label="Media Organizations" icon={<FaBriefcase />} />
            <StatCard number="3+" label="Countries Reached" icon={<FaGlobe />} />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black w-full">
        <div className="w-full mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
              Process
            </div>
            <h2 className="text-4xl font-bold mb-4 text-white">How Truth Nexus Works</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our comprehensive approach combines advanced AI models with human expertise
              to provide accurate information verification.
            </p>
          </motion.div>
          
          <div className="relative">
            {/* Process connector line */}
            <div className="absolute top-24 left-[calc(50%-1px)] w-2 bg-truth/30 h-[calc(100%-6rem)] hidden md:block"></div>
            
            <div className="space-y-24">
              <ProcessStep 
                number="01"
                title="AI-Powered Detection"
                description="Our advanced algorithms continuously scan digital content across the web, identifying potential misinformation based on linguistic patterns, source credibility, and content inconsistencies."
                isLeft={true}
              />
              
              <ProcessStep 
                number="02"
                title="Multi-Factor Verification"
                description="Each flagged piece of content undergoes a rigorous verification process that examines source credibility, cross-references with established facts, and applies contextual analysis."
                isLeft={false}
              />
              
              <ProcessStep 
                number="03"
                title="User-Friendly Reporting"
                description="Results are presented in clear, accessible formats that help users understand not just what information is questionable, but why it's been flagged and how to evaluate similar content."
                isLeft={true}
              />
              
              <ProcessStep 
                number="04"
                title="Continuous Learning"
                description="Our AI models continuously improve through machine learning, adapting to new misinformation tactics and evolving digital environments to stay ahead of bad actors."
                isLeft={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <TeamSection />

      {/* Ready to Combat Misinformation Section */}
      <section className="py-20 relative w-full">
        <div className="absolute inset-0 bg-truth/10 backdrop-blur-sm z-0"></div>
        <div className="w-[95%] md:w-[90%] lg:max-w-[1500px] mx-auto px-4 text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">Ready to Combat Misinformation?</h2>
            <p className="text-xl mb-8 text-gray-300 max-w-3xl mx-auto">
              Join thousands of journalists, researchers, and concerned citizens in the fight against digital misinformation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="bg-truth hover:bg-truth-dark text-lg group">
                <Link to="/auth/register" className="flex items-center gap-2">
                  Get Started 
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <FaRocket className="transition-transform group-hover:translate-x-1" />
                  </motion.div>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg">
                <Link to="/learn">Learn More</Link>
              </Button>
            </div>
            
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

const FeatureCard = ({ icon, title, description, delay = 0 }: { 
  icon: React.ReactNode, 
  title: string, 
  description: string,
  delay?: number
}) => (
  <motion.div 
    className="p-6 glass-card border border-truth/20 rounded-lg hover:border-truth/50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.6 }}
  >
    <div className="text-truth text-3xl mb-4 bg-truth/10 w-16 h-16 rounded-full flex items-center justify-center border border-truth/20 glow pulse-glow">{icon}</div>
    <h3 className="text-xl font-bold mb-2 text-shadow-sm">{title}</h3>
    <p className="text-gray-300">{description}</p>
  </motion.div>
);

const StatCard = ({ number, label, icon }: { number: string, label: string, icon: React.ReactNode }) => (
  <motion.div 
    className="text-center p-6 glass-card border border-truth/20 rounded-lg hover:border-truth/50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
  >
    <div className="text-truth mb-4 flex justify-center">
      <div className="bg-truth/10 w-16 h-16 rounded-full flex items-center justify-center text-2xl border border-truth/30 glow pulse-glow">
        {icon}
      </div>
    </div>
    <div className="text-4xl md:text-5xl font-bold text-white mb-2 text-shadow-sm">{number}</div>
    <div className="text-gray-300">{label}</div>
  </motion.div>
);

const ProcessStep = ({ number, title, description, isLeft }: { 
  number: string,
  title: string, 
  description: string,
  isLeft: boolean
}) => (
  <div className="flex flex-col md:flex-row items-center">
    <motion.div 
      className={`md:w-1/2 ${isLeft ? "md:text-right md:pr-16" : "md:order-1 md:text-left md:pl-16"}`}
      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <div className={`flex items-center ${isLeft ? "justify-end" : "justify-start"} mb-4`}>
        <div className="text-sm text-truth font-mono bg-truth/10 px-2 py-1 rounded">Step {number}</div>
      </div>
      <h3 className="text-2xl font-bold mb-2 text-shadow-sm">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
    
    <motion.div 
      className={`w-12 h-12 bg-truth/20 border-2 border-truth rounded-full flex items-center justify-center text-truth my-6 md:my-0 relative z-10 md:mx-auto glow pulse-glow ${isLeft ? "" : "md:order-0"}`}
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <FaLightbulb className="text-shadow-sm" />
    </motion.div>
    
    <div className={`md:w-1/2 ${isLeft ? "md:order-1" : ""}`}></div>
  </div>
);

const TestimonialCard = ({ quote, author, role }: { quote: string, author: string, role: string }) => (
  <motion.div 
    className="glass-card border border-truth/20 rounded-lg p-6 shadow-lg hover:shadow-xl hover:border-truth/40 transition-all"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
  >
    <div className="text-truth text-4xl mb-4 opacity-80 glow-text">"</div>
    <p className="text-gray-300 italic mb-4 leading-relaxed">{quote}</p>
    <div className="border-t border-truth/20 pt-4">
      <p className="font-medium text-white text-shadow-sm">{author}</p>
      <p className="text-sm text-gray-300">{role}</p>
    </div>
  </motion.div>
);

const FaGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" width="1em" height="1em" fill="currentColor">
    <path d="M336.5 160C322 70.7 287.8 8 248 8s-74 62.7-88.5 152h177zM152 256c0 22.2 1.2 43.5 3.3 64h185.3c2.1-20.5 3.3-41.8 3.3-64s-1.2-43.5-3.3-64H155.3c-2.1 20.5-3.3 41.8-3.3 64zm324.7-96c-28.6-67.9-86.5-120.4-158-141.6 24.4 33.8 41.2 84.7 50 141.6h108zM177.2 18.4C105.8 39.6 47.8 92.1 19.3 160h108c8.7-56.9 25.5-107.8 49.9-141.6zM487.4 192H372.7c2.1 21 3.3 42.5 3.3 64s-1.2 43-3.3 64h114.6c5.5-20.5 8.6-41.8 8.6-64s-3.1-43.5-8.5-64zM120 256c0-21.5 1.2-43 3.3-64H8.6C3.2 212.5 0 233.8 0 256s3.2 43.5 8.6 64h114.6c-2-21-3.2-42.5-3.2-64zm39.5 96c14.5 89.3 48.7 152 88.5 152s74-62.7 88.5-152h-177zm159.3 141.6c71.4-21.2 129.4-73.7 158-141.6h-108c-8.8 56.9-25.6 107.8-50 141.6zM19.3 352c28.6 67.9 86.5 120.4 158 141.6-24.4-33.8-41.2-84.7-50-141.6h-108z"/>
  </svg>
);

export default Index;
